import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import MicIcon from '@material-ui/icons/Mic';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import PauseIcon from '@material-ui/icons/Pause';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import CloseIcon from '@material-ui/icons/Close';
import ScheduleIcon from '@material-ui/icons/Schedule';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import DoneIcon from '@material-ui/icons/Done';
import DoneAllIcon from '@material-ui/icons/DoneAll';
import GroupIcon from '@material-ui/icons/Group';

import { IconButton } from '../../material-ui/IconButton';
import { TextField } from '../../material-ui/TextField';
import { ArrowBack } from '../../material-ui/ArrowBack';
import { Send } from '../../../icons';
import { useStyles } from '../useStyles';
import {
    ACCEPT_ATTR,
    MAX_ATTACHMENTS,
    isAudioAttachment,
    isImageAttachment,
    audioBlobUrlFromBase64,
    revokeAudioBlobUrl,
    readBlobAsAttachment,
    readFileAsAttachment,
    validateFile,
    validateVoiceBlob,
    voiceFileName,
} from '../attachments';
import { formatRecordingTime, useVoiceRecorder } from '../useVoiceRecorder';

const formatTime = (time) => {
    if (!time) {
        return '';
    }

    const date = new Date(time);
    const clock = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    // В пределах сегодняшнего дня показываем только время, иначе — дату и время (ТЗ 7.2).
    if (date.toDateString() === new Date().toDateString()) {
        return clock;
    }

    return `${date.toLocaleDateString([], { day: '2-digit', month: '2-digit', year: '2-digit' })} ${clock}`;
};

const VoiceAttachment = memo(({ src, attachment, classes, translate, onOpenFile }) => {
    const audioRef = useRef(null);
    const [playing, setPlaying] = useState(false);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        setPlaying(false);
        setFailed(false);
    }, [src]);

    useEffect(() => {
        const audio = audioRef.current;

        if (!audio) {
            return undefined;
        }

        const handlePlay = () => setPlaying(true);
        const handlePause = () => setPlaying(false);
        const handleEnded = () => setPlaying(false);
        const handleError = () => setFailed(true);

        audio.addEventListener('play', handlePlay);
        audio.addEventListener('pause', handlePause);
        audio.addEventListener('ended', handleEnded);
        audio.addEventListener('error', handleError);

        return () => {
            audio.removeEventListener('play', handlePlay);
            audio.removeEventListener('pause', handlePause);
            audio.removeEventListener('ended', handleEnded);
            audio.removeEventListener('error', handleError);
        };
    }, [src]);

    const handleToggle = useCallback(() => {
        const audio = audioRef.current;

        if (!audio || failed) {
            return;
        }

        if (playing) {
            audio.pause();
            return;
        }

        audio.play().catch(() => setFailed(true));
    }, [playing, failed]);

    if (failed) {
        return (
            <button type="button" className={classes.voicePlayer} onClick={() => onOpenFile && onOpenFile(attachment)}>
                <MicIcon fontSize="small" />
                <span className={classes.attachmentName}>{translate('messenger.voiceMessage')}</span>
            </button>
        );
    }

    return (
        <div className={classes.voicePlayer}>
            <IconButton size="small" className={classes.voicePlayButton} onClick={handleToggle} title={translate('messenger.playVoice')}>
                {playing ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
            </IconButton>
            <span className={classes.attachmentName}>{translate('messenger.voiceMessage')}</span>
            <audio ref={audioRef} src={src} preload="metadata" />
        </div>
    );
});

VoiceAttachment.displayName = 'MessengerVoiceAttachment';

const Attachment = memo(({ attachment, onPreviewImage, onOpenFile, getAttachmentData, classes, translate }) => {
    const isImage = isImageAttachment(attachment);
    const isAudio = isAudioAttachment(attachment);
    const imageLocalSrc = !isAudio
        ? attachment.preview || (attachment.data ? `data:${attachment.contentType};base64,${attachment.data}` : null)
        : null;
    const audioLocalSrc = isAudio
        ? attachment.preview ||
          (attachment.data
              ? audioBlobUrlFromBase64(attachment.data, attachment.contentType) ||
                `data:${attachment.contentType};base64,${attachment.data}`
              : null)
        : null;
    const localSrc = isAudio ? audioLocalSrc : imageLocalSrc;
    const [src, setSrc] = useState(localSrc);
    const audioBlobRef = useRef(null);

    const setAudioSrc = useCallback((contentType, data) => {
        if (audioBlobRef.current) {
            revokeAudioBlobUrl(audioBlobRef.current);
            audioBlobRef.current = null;
        }

        const blobUrl = audioBlobUrlFromBase64(data, contentType);

        if (blobUrl) {
            audioBlobRef.current = blobUrl;
            setSrc(blobUrl);
            return;
        }

        setSrc(`data:${contentType};base64,${data}`);
    }, []);

    useEffect(
        () => () => {
            if (audioBlobRef.current) {
                revokeAudioBlobUrl(audioBlobRef.current);
            }
        },
        []
    );

    // Для фото/аудио без локальных данных (полученные сообщения) лениво подгружаем
    // содержимое по id и кешируем — чтобы показать превью прямо в ленте.
    useEffect(() => {
        let cancelled = false;

        if ((isImage || isAudio) && !src && attachment.id != null && getAttachmentData) {
            getAttachmentData(attachment).then((full) => {
                if (!cancelled && full && full.data) {
                    if (isAudio) {
                        setAudioSrc(full.contentType || attachment.contentType, full.data);
                    } else {
                        setSrc(`data:${full.contentType || attachment.contentType};base64,${full.data}`);
                    }
                }
            });
        }

        return () => {
            cancelled = true;
        };
    }, [isImage, isAudio, src, attachment, getAttachmentData, setAudioSrc]);

    if (isAudio) {
        if (src) {
            return <VoiceAttachment src={src} attachment={attachment} classes={classes} translate={translate} onOpenFile={onOpenFile} />;
        }

        return (
            <span className={classes.attachmentChip}>
                <MicIcon fontSize="small" />
                <span className={classes.attachmentName}>{translate('messenger.voiceMessage')}</span>
            </span>
        );
    }

    if (isImage) {
        if (src) {
            return (
                <img
                    className={classes.attachmentImage}
                    src={src}
                    alt={attachment.name}
                    onClick={() => onPreviewImage && onPreviewImage(src, attachment.name)}
                />
            );
        }

        // Данные ещё подгружаются — показываем компактную заглушку.
        return (
            <span className={classes.attachmentChip}>
                <InsertDriveFileIcon fontSize="small" />
                <span className={classes.attachmentName}>{attachment.name}</span>
            </span>
        );
    }

    return (
        <button type="button" className={classes.attachmentChip} onClick={() => onOpenFile && onOpenFile(attachment)}>
            <InsertDriveFileIcon fontSize="small" />
            <span className={classes.attachmentName}>{attachment.name}</span>
        </button>
    );
});

Attachment.displayName = 'MessengerAttachment';

/*
 * Статус доставки исходящего сообщения (как в мессенджерах):
 *   - отправляется (в очереди / нет сети) — часы;
 *   - не отправлено (сервер отклонил)     — восклицательный знак;
 *   - доставлено (принято сервером/IS)    — одна галочка ✓;
 *   - прочитано (адресат открыл)          — две галочки ✓✓ (акцентный цвет).
 * Для входящих сообщений статус не показываем.
 */
const MessageStatus = memo(({ message, classes, translate }) => {
    if (!message.isOwn) {
        return null;
    }

    if (message.status === 'queued') {
        return <ScheduleIcon className={classes.statusIcon} titleAccess={translate('messenger.statusSending')} />;
    }

    if (message.status === 'failed') {
        return (
            <ErrorOutlineIcon
                className={classnames(classes.statusIcon, classes.statusFailed)}
                titleAccess={translate('messenger.statusFailed')}
            />
        );
    }

    if (message.isRead) {
        return (
            <DoneAllIcon className={classnames(classes.statusIcon, classes.statusRead)} titleAccess={translate('messenger.statusRead')} />
        );
    }

    return <DoneIcon className={classes.statusIcon} titleAccess={translate('messenger.statusDelivered')} />;
});

MessageStatus.displayName = 'MessengerMessageStatus';

export const ThreadView = memo(
    ({
        chat,
        messages,
        isNarrow,
        bottomInset,
        onBack,
        onSend,
        onOpenAttachment,
        getAttachmentData,
        onPreviewImage,
        onOpenMembers,
        className,
    }) => {
        const classes = useStyles();
        const { t: translate } = useTranslation();
        const [text, setText] = useState('');
        const [attachments, setAttachments] = useState([]);
        const [error, setError] = useState(null);
        const [dockHeight, setDockHeight] = useState(64);
        const endRef = useRef(null);
        const fileInputRef = useRef(null);
        const dockRef = useRef(null);

        useEffect(() => {
            if (endRef.current) {
                endRef.current.scrollIntoView({ behavior: 'smooth' });
            }
        }, [messages]);

        useEffect(() => {
            if (!isNarrow || !chat || !dockRef.current || typeof ResizeObserver === 'undefined') {
                return undefined;
            }

            const node = dockRef.current;
            const observer = new ResizeObserver(() => {
                setDockHeight(node.offsetHeight || 64);
            });

            observer.observe(node);
            setDockHeight(node.offsetHeight || 64);

            return () => observer.disconnect();
        }, [isNarrow, chat, attachments.length, error]);

        const handleVoiceRecorded = useCallback((blob, contentType) => {
            const validationError = validateVoiceBlob(blob, contentType);

            if (validationError) {
                setError(validationError);
                return;
            }

            readBlobAsAttachment(blob, voiceFileName(contentType), contentType)
                .then((attachment) => {
                    setAttachments((prev) => [...prev, attachment].slice(0, MAX_ATTACHMENTS));
                    setError(null);
                })
                .catch(() => setError('voiceRecordFailed'));
        }, []);

        const handleVoiceError = useCallback((key) => setError(key), []);

        const voice = useVoiceRecorder({ onRecorded: handleVoiceRecorded, onError: handleVoiceError }) || {
            supported: false,
            recording: false,
            elapsedMs: 0,
            toggleRecording: () => {},
        };

        const isRecording = !!voice.recording;
        const canSend = !isRecording && (!!text.trim() || attachments.length > 0);

        const handleSend = useCallback(() => {
            if (!text.trim() && attachments.length === 0) {
                return;
            }

            onSend(text, attachments);
            setText('');
            setAttachments([]);
            setError(null);
        }, [text, attachments, onSend]);

        const handleKeyDown = useCallback(
            (event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    handleSend();
                }
            },
            [handleSend]
        );

        const handlePick = useCallback(() => fileInputRef.current && fileInputRef.current.click(), []);

        const handleFiles = useCallback((event) => {
            const files = Array.from(event.target.files || []);

            event.target.value = '';

            if (files.length === 0) {
                return;
            }

            let nextError = null;
            const valid = [];

            files.forEach((file) => {
                const validationError = validateFile(file);

                if (validationError) {
                    nextError = validationError;
                } else {
                    valid.push(file);
                }
            });

            setError(nextError);

            Promise.all(valid.map((file) => readFileAsAttachment(file))).then((read) => {
                setAttachments((prev) => [...prev, ...read].slice(0, MAX_ATTACHMENTS));
            });
        }, []);

        const handleRemove = useCallback((index) => setAttachments((prev) => prev.filter((_, i) => i !== index)), []);

        const dockPaddingBottom = `calc(${bottomInset || 0}px + env(safe-area-inset-bottom, 0px))`;
        const messagesPaddingBottom = isNarrow ? dockHeight + (bottomInset || 0) + 8 : undefined;

        const pendingBlock =
            attachments.length > 0 || error ? (
                <div className={classnames(classes.pendingAttachments, isNarrow && classes.pendingAttachmentsDock)}>
                    {attachments.map((attachment, index) => (
                        <span key={`p_${index}`} className={classes.pendingChip}>
                            <span className={classes.attachmentName}>
                                {isAudioAttachment(attachment) ? translate('messenger.voiceMessage') : attachment.name}
                            </span>
                            <IconButton size="small" onClick={() => handleRemove(index)}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </span>
                    ))}
                    {error && <span className={classes.attachmentError}>{translate(`messenger.${error}`)}</span>}
                </div>
            ) : null;

        const composerBlock = (
            <div className={classes.composer}>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPT_ATTR}
                    multiple={true}
                    style={{ display: 'none' }}
                    onChange={handleFiles}
                />
                <IconButton onClick={handlePick} disabled={isRecording} title={translate('messenger.attach')}>
                    <AttachFileIcon />
                </IconButton>
                {voice.supported && (
                    <>
                        <IconButton
                            className={isRecording ? classes.voiceRecordActive : undefined}
                            onClick={voice.toggleRecording}
                            title={isRecording ? translate('messenger.stopRecording') : translate('messenger.recordVoice')}
                        >
                            <MicIcon />
                        </IconButton>
                        {isRecording && <span className={classes.recordingTimer}>{formatRecordingTime(voice.elapsedMs)}</span>}
                    </>
                )}
                <TextField
                    className={classes.composerInput}
                    value={text}
                    onChange={(event) => setText(event.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={translate('messenger.inputPlaceholder')}
                    multiline={true}
                    maxRows={4}
                    fullWidth={true}
                />
                <IconButton color="primary" onClick={handleSend} disabled={!canSend}>
                    <Send width="24" height="24" />
                </IconButton>
            </div>
        );

        if (!chat) {
            return (
                <div className={classnames(className, classes.empty)}>
                    <span>{translate('messenger.selectChat')}</span>
                </div>
            );
        }

        return (
            <div className={className}>
                <div className={classes.threadHeader}>
                    {isNarrow && onBack && (
                        <IconButton className={classes.backButton} size="small" onClick={onBack}>
                            <ArrowBack />
                        </IconButton>
                    )}
                    <span className={classes.threadTitle}>{chat.title || translate('messenger.untitledChat')}</span>
                    {chat.isGroup && onOpenMembers && (
                        <IconButton size="small" onClick={() => onOpenMembers(chat)} title={translate('messenger.members')}>
                            <GroupIcon />
                        </IconButton>
                    )}
                </div>

                <div className={classes.messages} style={messagesPaddingBottom ? { paddingBottom: messagesPaddingBottom } : undefined}>
                    {messages && messages.length > 0 ? (
                        messages.map((message, index) => (
                            <div
                                key={message.id != null ? `m_${message.id}` : message.localId || index}
                                className={classnames(classes.bubble, message.isOwn ? classes.bubbleOwn : classes.bubbleIncoming)}
                            >
                                {!message.isOwn && message.author && <div className={classes.bubbleAuthor}>{message.author}</div>}
                                {message.text && <div>{message.text}</div>}
                                {!message.text && !(message.attachments && message.attachments.length > 0) && (
                                    <div className={classes.bubbleEmpty}>{translate('messenger.emptyMessage')}</div>
                                )}
                                {message.attachments && message.attachments.length > 0 && (
                                    <div className={classes.bubbleAttachments}>
                                        {message.attachments.map((attachment, i) => (
                                            <Attachment
                                                key={attachment.id != null ? `a_${attachment.id}` : `a_${i}`}
                                                attachment={attachment}
                                                onOpenFile={onOpenAttachment}
                                                onPreviewImage={onPreviewImage}
                                                getAttachmentData={getAttachmentData}
                                                classes={classes}
                                                translate={translate}
                                            />
                                        ))}
                                    </div>
                                )}
                                <div className={classes.bubbleMeta}>
                                    <span>{formatTime(message.time || message.createdAt)}</span>
                                    <MessageStatus message={message} classes={classes} translate={translate} />
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className={classes.empty}>{translate('messenger.noMessages')}</div>
                    )}
                    <div ref={endRef} />
                </div>

                {isNarrow ? (
                    <div ref={dockRef} className={classes.composerDock} style={{ paddingBottom: dockPaddingBottom }}>
                        {pendingBlock}
                        {composerBlock}
                    </div>
                ) : (
                    <>
                        {pendingBlock}
                        {composerBlock}
                    </>
                )}
            </div>
        );
    }
);

ThreadView.displayName = 'ThreadView';
